<script lang="ts" setup>
  import { type PropType } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import { useHttpClient, useTranslator, useRoute } from '@othercommerce/foundation';
  import type { Pagination } from '@storefront/types';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { DictionaryRecord } from '@othercommerce/domain';
  import { ReferenceType } from '@othercommerce/domain';

  interface RelatedDocument {
    referenceType: ReferenceType,
    documentDate: string,
    documentNumber: string,
  }

  type Settlement = {
    documentType: DictionaryRecord;
    documentNumber: string;
    documentDate: string;
    currencyCode: string;
    paymentDate: string | null;
    value: string;
    settled: string;
    remaining: string;
    isOverdue: boolean;
    payable: boolean;
    download: string | null;
    settlementDocumentReferences: RelatedDocument[];
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps({
    settlements: { type: Object as PropType<Pagination<Settlement>>, required: true },
    filters: { type: Object as PropType<any>, required: true },
    credit: { type: String, required: true },
    debit: { type: String, required: true },
    creditInRange: { type: String, required: true },
    debitInRange: { type: String, required: true },
  });

  const breakpoints = useBreakpoints();
  const http = useHttpClient();
  const translator = useTranslator();
  const route = useRoute();

  const settledOptions = [
    { value: false, label: translator.trans('SHOP.COMMON.LABEL.NO') },
    { value: true, label: translator.trans('SHOP.COMMON.LABEL.YES') },
  ];

  const payableOptions = [
    { value: true, label: translator.trans('SHOP.COMMON.LABEL.CREDIT') },
    { value: false, label: translator.trans('SHOP.COMMON.LABEL.DEBIT') },
  ];

  function handleFilters(data: any) {
    return http.get(route('web.account.settlements.index', data));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.SETTLEMENTS') }}
  </AccountMobileNavigation>
  <SfTile class="account-settlements" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">{{ $t('SHOP.ACCOUNT.HEADER.SETTLEMENTS') }}</h1>
    </template>
    <template #default>
      <div class="settlement-summary">
        <div class="item">
          <h2>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TOTAL_CREDIT') }}</h2>
          <SfMoney :value="credit" />
        </div>
        <div class="item">
          <h2>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TOTAL_DEBIT') }}</h2>
          <SfMoney :value="debit" />
        </div>
      </div>
      <div class="account-settlements-filters">
        <FormController v-slot="{ submit }" :data="filters" :onSubmit="handleFilters">
          <div class="col">
            <SfFormRadio :label="$t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TYPE')" :options="payableOptions" name="payable" @change="submit" />
          </div>
          <div class="col">
            <SfFormRadio :label="$t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_SETTLED')" :options="settledOptions" name="settled" @change="submit" />
          </div>
          <div class="col">
            <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_FROM')" name="from" type="date" @change="submit" />
          </div>
          <div class="col">
            <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TO')" name="to" type="date" @change="submit" />
          </div>
        </FormController>
      </div>
      <div v-if="settlements.data.length > 0" class="account-settlements__content">
        <SfListCard v-for="settlement in settlements.data" :key="settlement.documentNumber" class="account-settlements">
          <template #icons>
            <span v-if="settlement.paymentDate" :class="{ 'account-settlements__overdue': settlement.isOverdue }" class="account-settlements__count">
              {{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENT_PAYMENT_DUE') }}
              <SfDateTime :value="settlement.paymentDate" />
            </span>
          </template>
          <template #default>
            <div class="account-settlements__header">
              <h2 class="account-settlements__heading">
                {{ settlement.documentType.label }} {{ settlement.documentNumber }}
              </h2>
              <p class="account-settlements__date">
                {{ $t('SHOP.DOCUMENT.LABEL.DATE') }}
                <SfDateTime :value="settlement.documentDate" />
              </p>
            </div>
            <div class="account-settlements__details">
              <span v-for="related in settlement.settlementDocumentReferences" class="account-settlements__related">
                <span>
                  <small>{{ related.referenceType === ReferenceType.CORRECTION ? $t('SHOP.DOCUMENT.LABEL.CORRECTION_TO') : $t('SHOP.DOCUMENT.LABEL.RELATED_TO') }}</small>
                </span>&nbsp;
                <span>
                  {{ related.documentNumber }}
                </span>&nbsp;
                <span>
                  <small>{{ $t('SHOP.DOCUMENT.LABEL.DATE') }}</small>
                </span>&nbsp;
                <SfDateTime :value="related.documentDate" />
              </span>
              <span class="account-settlements__separator" />
              <span class="account-settlements__price">
                <span>
                  <small>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TOTAL_SETTLED') }}</small>
                </span>&nbsp;
                <SfMoney :code="settlement.currencyCode" :value="settlement.settled" />&nbsp;
                <span>
                  <small>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TOTAL_SETTLED_FROM') }}</small>
                </span>&nbsp;
                <SfMoney :code="settlement.currencyCode" :value="settlement.value" />
              </span>
              <span class="account-settlements__price">
                <span>
                  <small>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_TOTAL_REMAINING') }}</small>
                </span>&nbsp;
                <SfMoney :code="settlement.currencyCode" :value="settlement.remaining" />
              </span>
            </div>
          </template>
          <template #actions>
            <SfButton :disabled="!settlement.download" :href="settlement.download || ''" small target="_blank">
              {{ settlement.download ? $t('SHOP.ACCOUNT.ACTION.DOCUMENT_DOWNLOAD') : $t('SHOP.ACCOUNT.ACTION.DOCUMENT_UNAVAILABLE') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.SETTLEMENTS_NO_DATA') }}
      </SfDataPlaceholder>
      <div class="settlement-summary-small">
        <div class="item label">
          <h2>{{ $t('SHOP.ACCOUNT.HEADER.SETTLEMENTS_IN_RANGE') }}</h2>
        </div>
        <div class="item">
          <h3>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_IN_RANGE_CREDIT') }}</h3>
          <SfMoney :value="creditInRange" />
        </div>
        <div class="item">
          <h3>{{ $t('SHOP.ACCOUNT.LABEL.SETTLEMENTS_IN_RANGE_DEBIT') }}</h3>
          <SfMoney :value="debitInRange" />
        </div>
      </div>
      <SfPagination :pagination="settlements" />
    </template>
  </SfTile>

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .account-settlements-filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2rem;
  }

  .settlement-summary {
    display: flex;
    flex-wrap: wrap;
    column-gap: 4rem;
    row-gap: 1rem;

    .item {
      font-size: 3rem;
      font-weight: var(--font-weight-bold);

      h2 {
        font-size: 0.875rem !important;
      }
    }
  }

  .settlement-summary-small {
    display: flex;
    flex-wrap: wrap;
    column-gap: 4rem;
    row-gap: 1rem;
    line-height: 1.25rem;

    .item {
      display: flex;
      font-size: 1rem;
      font-weight: var(--font-weight-bold);
      gap: 1rem;

      &.label {
        margin-right: auto;
      }

      h2, h3 {
        font-size: 0.875rem !important;
      }

      h3 {
        font-weight: var(--font-weight-regular);
      }
    }
  }

  .account-settlements {
    .list-card__content-default {
      gap: 0;
    }

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__details {
      display: flex;
      flex-direction: column;

      small {
        font-weight: var(--font-weight-regular);
      }
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__date,
    &__count {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }

    &__overdue {
      font-weight: var(--font-weight-bold);
      color: var(--danger-color);
    }

    &__related {
      font-weight: var(--font-weight-bold);
      margin-top: 0.125rem;
      font-size: 0.875rem;
    }

    &__separator {
      display: block;
      margin: 0.5rem 0;
      background: var(--secondary-border);
      height: 1px;
      width: 100%;
    }

    &__price {
      font-weight: var(--font-weight-bold);
      margin-top: 0.125rem;
    }
  }

  @include media.query(lg) {
    .account-settlements {
      row-gap: 1rem;

      &__content :deep(.list-card__actions) {
        gap: 0.5rem;
      }
    }
  }
</style>
